import {
  DsHeaderTagConfiguration,
  DsHeaderUserInfo,
  DsNavigationItem,
  DsNavigationBarModule,
  DsHeaderModule,
  DsBoxModule,
  DsButtonModule,
  DsIconModule,
  DsToastModule,
  DsProgressCircleModule,
} from '@bmw-ds/components';
import {
  ApplicationRef,
  Component,
  DestroyRef,
  inject,
  OnInit,
} from '@angular/core';
import { environment } from 'src/environments/environment';
import { Router, RouterModule } from '@angular/router';
import {
  DsLoadingAreaModule,
  DsLoadingAreaConfiguration,
  DsLoadingAreaService,
} from '@bmw-ds/components';
import { SharedService } from './services/shared-service';
import { BackendService } from './services/backend-service';
import { OAuthService } from 'angular-oauth2-oidc';
import {
  oauthConfigurationForInternet,
  oauthConfigurationForIntranet,
} from './configs/oauth.config';
import {
  UserProfileData,
  WenOidcUserProfileResponseData,
} from './models/user-profile.model';
import {
  filter,
  firstValueFrom,
  interval,
  map,
  startWith,
  take,
  tap,
  timer,
} from 'rxjs';
import { AsyncPipe } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { DsMessageModule } from '@bmw-ds/components';

@Component({
  selector: 'app-root',
  standalone: true,
  imports: [
    DsNavigationBarModule,
    DsHeaderModule,
    DsBoxModule,
    DsButtonModule,
    DsToastModule,
    DsIconModule,
    DsLoadingAreaModule,
    RouterModule,
    AsyncPipe,
    DsProgressCircleModule,
    DsMessageModule,
  ],
  templateUrl: './app.component.html',
  styleUrl: './app.component.scss',
})
export class AppComponent implements OnInit {
  constructor() {
    timer(Number(environment.forceLogoutDurationinMinutes) * 60 * 1000)
      .pipe(
        tap(() => {
          this.sharedService.logger(
            'App Component constructor',
            `After ${Number(environment.forceLogoutDurationinMinutes)} minutes`
          );
          this.handleLogout();
        }),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe();
  }

  destroyRef = inject(DestroyRef);
  loadingAreaService = inject(DsLoadingAreaService);
  sharedService = inject(SharedService);
  backendService = inject(BackendService);
  oauthService = inject(OAuthService);
  router = inject(Router);
  appRef = inject(ApplicationRef);

  showLoader = false;
  private isRedirectingToLogin = false;

  forcedLogoutWarning: string = '';
  showForcedLogoutWarning$ = timer(
    Number(environment.forceLogoutDurationinMinutes - 5) * 60 * 1000
  ).pipe(
    map(() => {
      return true;
    }),
    startWith(false)
  );

  deploymentString = '4:38 PM 21 Nov';

  loadingAreaConfig: DsLoadingAreaConfiguration = {
    id: 'appLoader',
  };

  async ngOnInit() {
    interval(1_000)
      .pipe(startWith(0), takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.checkForSessionExpiry();
      });
    this.sharedService.logger(
      'App Component app initialized' + this.deploymentString,
      null
    );
    this.forcedLogoutWarning = `You will be logged out of this application after 5 minutes (at ${new Date(new Date().setMinutes(new Date().getMinutes() + 30)).toLocaleTimeString('en-US', { hour: 'numeric', minute: 'numeric', hour12: true })}). Please submit any changes made.`;
    this.sharedService.logger('App Component onInit', [
      environment.forceLogoutDurationinMinutes,
      this.forcedLogoutWarning,
    ]);
    this.sharedService.showUIElementsSubject.next(false);
    this.loadingAreaService.addLoadingArea(this.loadingAreaConfig);
    this.showLoader = true;
    // localStorage.setItem('ds-theme', 'dark');

    this.sharedService.showUIElements$.subscribe((response) => {
      if (response === true) {
        this.loadingAreaService.removeLoadingArea(this.loadingAreaConfig.id);
        this.showLoader = false;
      }
    });

    try {
      // NOTE - Earlier localhost:4200 was not whitelisted in WEN, but now it is whitelisted

      // if (
      //   environment.currentEnvironment === 'production' ||
      //   environment.currentEnvironment === 'integration'
      // ) {
      const responseFromWenAuthentication = await this.initializeSingleSignOn();
      if (this.isRedirectingToLogin) {
        return;
      }
      this.sharedService.logger(
        'App Component response from WEN',
        responseFromWenAuthentication
      );
      if (responseFromWenAuthentication?.accessToken) {
        const responseFromVa = await this.getAndSetEmployeeDetailsFromVa();
        this.sharedService.logger(
          'App Component response from VA',
          responseFromVa
        );
        if (responseFromVa) {
          this.setNavigationItems(responseFromVa?.roles);
        } else {
          this.sharedService.logger(
            'App Component no response from VA',
            responseFromVa
          );
        }
      } else {
        this.sharedService.logger(
          'App Component no response from WEN OIDC',
          responseFromWenAuthentication
        );
        throw Error(
          'Unable to connect with WEN OIDC authorization server. Please try again later.'
        );
      }
      // } else {
      //   await this.hardcodeTestData();
      // }
    } catch (error) {
      this.sharedService.logger('App Component error in onInit', error);
      this.handleError(error);
    } finally {
      if (!this.isRedirectingToLogin) {
        this.sharedService.showUIElementsSubject.next(true);
      }
    }
  }

  environmentTagConfig: DsHeaderTagConfiguration = {
    label: environment.currentEnvironment.toUpperCase(),
    tone: 'info',
  };

  userInfo: DsHeaderUserInfo = {
    username: '',
    department: '',
    strongAuth: false,
  };

  navigationItems: DsNavigationItem[] = [];

  handleLogout() {
    this.sharedService.logger('App Component handleLogout called', null);
    this.oauthService.logOut();
    this.sharedService.clearStorage();
    // window.location.reload();
  }

  // NOTE - Add QXID, access_token to work locally
  async hardcodeTestData() {
    // eslint-disable-next-line no-useless-catch
    try {
      const userProfile: UserProfileData = {
        firstName: '',
        middleName: '',
        lastName: '',
        fullName: '',
        qxid: '',
        employeeId: '00655254',
        email: '',
        hrStatus: '',
        companyCode: '',
        hrCompany: '',
        hireDate: '',
        roles: ['CCSP_Admin', 'CCSP_Specialist'],
        departmentNumber: '',
        employeeType: '',
        expiresAt: Number(
          sessionStorage.getItem('expires_at') ?? new Date().getTime()
        ),
        accessToken: sessionStorage.getItem('access_token') ?? '',
      };

      this.sharedService.setUserProfile(userProfile);

      const employeeDetailsFromVa = await firstValueFrom(
        this.backendService.getEmployeeDetailsFromVa(userProfile.employeeId)
      );

      userProfile.email = employeeDetailsFromVa.employeeBo.email
        ?.toLowerCase()
        .trim();
      userProfile.firstName =
        employeeDetailsFromVa.employeeBo?.firstName.trim();
      userProfile.middleName =
        employeeDetailsFromVa.employeeBo?.middleName.trim();
      userProfile.lastName = employeeDetailsFromVa.employeeBo?.lastName.trim();
      userProfile.fullName = `${userProfile.firstName.trim()} ${userProfile.middleName.trim()} ${userProfile.lastName.trim()} `;
      userProfile.hrStatus = employeeDetailsFromVa.employeeBo.hrStatus?.trim();
      userProfile.companyCode =
        employeeDetailsFromVa.employeeBo.companyCode?.trim();

      this.userInfo = {
        department: `${userProfile.departmentNumber.trim()} ${userProfile.employeeType.trim()}`,
        // username: `${userProfile.firstName.trim()} ${userProfile.lastName.trim()} (${userProfile.employeeId.trim() ?? ''} | ${userProfile.qxid.trim() ?? ''})`,
        username: `${userProfile.firstName.trim()} ${userProfile.lastName.trim()}`,
        strongAuth: false,
      };

      this.sharedService.setUserProfile(userProfile);
      this.setNavigationItems(userProfile.roles);
    } catch (error) {
      throw error;
    }
  }

  async initializeSingleSignOn(): Promise<UserProfileData | null> {
    this.sharedService.logger('App Component entered SSO', null);
    try {
      this.setOauthConfiguration();
      this.sharedService.logger('App Component SSO config complete', null);
      const docs = await this.oauthService.loadDiscoveryDocument();
      this.sharedService.logger('App Component SSO auth docs', docs);
      const isAuthenticated = await this.oauthService.tryLogin();
      this.sharedService.logger(
        'App Component auth complete?',
        isAuthenticated
      );

      let userProfile: UserProfileData | null = null;

      if (!this.oauthService.hasValidIdToken() || !isAuthenticated) {
        this.isRedirectingToLogin = true;
        this.oauthService.initLoginFlow(this.router.url);
        return null;
      }

      const claims = this.oauthService.getIdentityClaims();
      if (claims) {
        const userInfoFromWenAuthentication: WenOidcUserProfileResponseData =
          (await this.oauthService.loadUserProfile()) as WenOidcUserProfileResponseData;
        this.sharedService.logger(
          'App Comppnent response from WEN authentication',
          userInfoFromWenAuthentication
        );

        // Set session storage with user profile
        userProfile = {
          firstName:
            userInfoFromWenAuthentication.info.given_name?.trim() ?? '',
          middleName: '',
          lastName:
            userInfoFromWenAuthentication.info.family_name?.trim() ?? '',
          fullName: '',
          qxid:
            userInfoFromWenAuthentication.info.sub?.trim().toUpperCase() ?? '',
          employeeId:
            userInfoFromWenAuthentication.info.employeenumber?.trim() ?? '',
          email: '',
          hrStatus: '',
          companyCode: '',
          hrCompany: '',
          hireDate: '',
          roles: userInfoFromWenAuthentication.info.groups ?? [],
          departmentNumber:
            userInfoFromWenAuthentication.info.departmentnumber?.trim() ?? '',
          employeeType:
            userInfoFromWenAuthentication.info.employeetype?.trim() ?? '',
          expiresAt: Number(
            this.oauthService.getAccessTokenExpiration() ?? new Date().getTime()
          ),
          accessToken: this.oauthService.getAccessToken() ?? '',
        };
        this.sharedService.logger(
          'App Component initial user profile',
          userProfile
        );
        this.sharedService.setUserProfile(userProfile);
      }
      this.sharedService.logger('App Component complete SSO', null);
      return userProfile;
    } catch (error) {
      this.sharedService.logger('App Component error in SSO', error);
      throw error;
    }
  }

  async getAndSetEmployeeDetailsFromVa(): Promise<UserProfileData | null> {
    this.sharedService.logger('App Component fetching VA', null);

    try {
      const storedUserProfile: UserProfileData = (await firstValueFrom(
        this.sharedService.userProfile$.pipe(
          filter((p): p is UserProfileData => !!p),
          take(1)
        )
      )) as UserProfileData;

      this.sharedService.logger('Stored user profile', storedUserProfile);

      const employeeId = storedUserProfile.employeeId;
      let sanitizedEmployeeId = String(employeeId.trim()).padStart(8, '0');

      // If no employee ID in production, throw error
      if (!employeeId && environment.currentEnvironment === 'production') {
        throw Error(
          `You don't have access (invalid employee ID from WEN authentication server - ${storedUserProfile?.employeeId}) to use this application.`
        );
      }

      // If no employee ID in non-production, hardcode and set employee ID
      if (!employeeId && environment.currentEnvironment !== 'production') {
        const employeeNumberToHardcode = (
          await firstValueFrom(
            this.backendService.getParameterMaintenanceData()
          )
        ).filter((data) => {
          return data.paramName === 'EmployeeID';
        })[0].paramValue;

        sanitizedEmployeeId = String(employeeNumberToHardcode?.trim()).padStart(
          8,
          '0'
        );
        this.sharedService.logger(
          'App Component using hardcoded employee ID in integration',
          null
        );
      }

      // If no employee roles, throw error
      if (!storedUserProfile.roles || !storedUserProfile.roles.length) {
        throw Error(
          `You (${
            storedUserProfile.employeeId
          }) don't have access (invalid roles from WEN authentication server) to use this application.`
        );
      }

      const employeeDetailsFromVa = await firstValueFrom(
        this.backendService.getEmployeeDetailsFromVa(sanitizedEmployeeId)
      );

      this.sharedService.logger('App Component user profile from VA', [
        employeeDetailsFromVa,
        sanitizedEmployeeId,
      ]);

      // If no employeeId, throw error
      if (!employeeDetailsFromVa?.employeeBo?.employeeId) {
        throw Error(
          `You don't have access (invalid employee ID from VA - ${sanitizedEmployeeId}) to use this application.`
        );
      }

      // If HR status is not ACTIVE, throw error
      if (
        !employeeDetailsFromVa?.employeeBo?.hrStatus?.trim() ||
        employeeDetailsFromVa?.employeeBo?.hrStatus?.trim() !== 'ACTIVE'
      ) {
        throw Error(
          `You (employee ID - ${
            sanitizedEmployeeId || ''
          }) don't have access (inactive employee HR status from VA) to use this application.`
        );
      }

      const sanitizedEmployeeFullName = `${employeeDetailsFromVa?.employeeBo.firstName.trim()} ${employeeDetailsFromVa?.employeeBo.middleName?.trim()} ${employeeDetailsFromVa?.employeeBo.lastName.trim()}`;

      // If no valid full name, throw error
      if (!sanitizedEmployeeFullName) {
        throw Error(
          `You (employee ID - ${
            sanitizedEmployeeId || ''
          }) don't have access (invalid full name from VA) to use this application.`
        );
      }

      // Modify user profile in session storage
      const modifiedUserProfile: UserProfileData = {
        firstName: employeeDetailsFromVa?.employeeBo.firstName.trim() ?? '',
        middleName: employeeDetailsFromVa?.employeeBo.middleName.trim() ?? '',
        lastName: employeeDetailsFromVa?.employeeBo.lastName.trim() ?? '',
        fullName: sanitizedEmployeeFullName.trim(),
        qxid: storedUserProfile?.qxid.toUpperCase().trim() ?? '',
        email:
          employeeDetailsFromVa?.employeeBo?.email?.trim()?.toLowerCase() ||
          storedUserProfile?.email.toLowerCase().trim() ||
          '',
        hrStatus: employeeDetailsFromVa?.employeeBo?.hrStatus?.trim(),
        companyCode: employeeDetailsFromVa?.employeeBo.companyCode.trim() ?? '',
        hrCompany: employeeDetailsFromVa?.employeeBo.hrCompany.trim() ?? '',
        hireDate: employeeDetailsFromVa?.employeeBo.hireDate.trim() ?? '',
        roles: storedUserProfile?.roles ?? [],
        departmentNumber: storedUserProfile?.departmentNumber.trim() ?? '',
        employeeType: storedUserProfile?.employeeType.trim() ?? '',
        expiresAt: new Date().getTime(),
        accessToken: storedUserProfile?.accessToken ?? '',
        employeeId: sanitizedEmployeeId.trim(),
      };

      this.sharedService.logger('Final user profile', modifiedUserProfile);

      // Set user info in header
      this.userInfo = {
        department: `${modifiedUserProfile.departmentNumber} ${
          modifiedUserProfile.employeeType
        }`,
        // username: `${modifiedUserProfile.firstName} ${modifiedUserProfile.lastName} (${modifiedUserProfile.employeeId} | ${modifiedUserProfile.qxid})`,
        username: `${modifiedUserProfile.firstName} ${modifiedUserProfile.lastName}`,
        strongAuth: false,
      };
      this.sharedService.setUserProfile(modifiedUserProfile);

      return modifiedUserProfile;
    } catch (error) {
      this.sharedService.logger('App Component error in VA API', error);
      throw error;
      // return null;
    }
  }

  checkForSessionExpiry() {
    const expirationTime = new Date(sessionStorage.getItem('expires_at') || '');
    const sessionExpired =
      (+expirationTime < 1e12 ? +expirationTime * 1000 : +expirationTime) <
      Date.now();

    if (sessionExpired) {
      this.sharedService.clearStorage();
      this.handleLogout();
    }
  }

  setOauthConfiguration() {
    const currentUrl: string = window.location.href;
    if (currentUrl.includes('.bmw.cloud')) {
      this.oauthService.configure(oauthConfigurationForInternet);
    } else {
      this.oauthService.configure(oauthConfigurationForIntranet);
    }
  }

  handleError(error: unknown) {
    this.sharedService.logger('App Component error handleError', error);

    if (error instanceof HttpErrorResponse) {
      if (error.status === 401 || error.status === 403) {
        this.sharedService.errorMessageSubject.next(
          'Please check credentials and try again.'
        );
        this.router.navigate(['error']);
        return;
      } else if (error.status >= 500) {
        this.sharedService.errorMessageSubject.next(
          'Unable to process your request. Please try again.'
        );
        this.router.navigate(['error']);
        return;
      } else {
        this.sharedService.errorMessageSubject.next(
          'Some error occured. Please try again.'
        );
        this.router.navigate(['error']);
        return;
      }
    } else if (error instanceof Error && error.message) {
      this.sharedService.errorMessageSubject.next(error.message);
      this.router.navigate(['error']);
      return;
    } else {
      this.sharedService.errorMessageSubject.next(
        'Something went wrong while loading application. Please try again.'
      );
      this.router.navigate(['error']);
      return;
    }
  }

  // setNavigationItems(roles: string[]) {
  //   this.navigationItems = [
  //     {
  //       label: 'User',
  //       group: true,
  //       children: [
  //         {
  //           label: 'Home',
  //           routerLink: '/home',
  //           icon: 'home',
  //         },
  //         {
  //           label: 'Error',
  //           routerLink: '/error',
  //           icon: 'error',
  //         },
  //         {
  //           label: 'My Profile',
  //           routerLink: '/my-profile',
  //           icon: 'user',
  //         },
  //         {
  //           label: 'Employee Info. & Driver License',
  //           routerLink: '/employee-information-and-driver-license',
  //           icon: 'parent',
  //         },
  //       ],
  //     },
  //     {
  //       label: 'Maintenance',
  //       group: true,
  //       children: [
  //         {
  //           label: 'Country State Code Maintenance',
  //           routerLink: '/country-state-code-maintenance',
  //           icon: 'globe',
  //         },
  //         {
  //           label: 'Model Code Maintenance',
  //           routerLink: '/model-code-maintenance',
  //           icon: 'list_edit',
  //         },
  //         {
  //           label: 'Parameter Maintenance',
  //           routerLink: '/parameter-maintenance',
  //           icon: 'database_edit',
  //         },
  //         {
  //           label: 'Relationship Maintenance',
  //           routerLink: '/relationship-maintenance',
  //           icon: 'group_edit',
  //         },
  //         {
  //           label: 'MVR Score',
  //           routerLink: '/mvr-score',
  //           icon: 'line_chart',
  //         },
  //       ],
  //     },
  //     {
  //       label: 'Administration',
  //       group: true,
  //       children: [
  //         {
  //           label: 'Consent Administration',
  //           routerLink: '/consent-administration',
  //           icon: 'file_user',
  //         },
  //         {
  //           label: 'Vehicle History',
  //           routerLink: '/vehicle-history',
  //           icon: 'calendar_car',
  //         },
  //         {
  //           label: 'Vehicle Requests',
  //           routerLink: '/vehicle-requests',
  //           icon: 'file_list',
  //         },
  //       ],
  //     },
  //   ];

  //   const childrenRoutes = [
  //     {
  //       label: 'Consent Administration',
  //       routerLink: '/consent-administration',
  //       icon: 'file_user',
  //     },
  //     {
  //       label: 'Vehicle History',
  //       routerLink: '/vehicle-history',
  //       icon: 'calendar_car',
  //     },
  //     {
  //       label: 'Vehicle Requests',
  //       routerLink: '/vehicle-requests',
  //       icon: 'file_list',
  //     },
  //     {
  //       label: 'Country State Code Maintenance',
  //       routerLink: '/country-state-code-maintenance',
  //       icon: 'globe',
  //     },
  //     {
  //       label: 'Model Code Maintenance',
  //       routerLink: '/model-code-maintenance',
  //       icon: 'list_edit',
  //     },
  //     {
  //       label: 'Parameter Maintenance',
  //       routerLink: '/parameter-maintenance',
  //       icon: 'database_edit',
  //     },
  //     {
  //       label: 'Relationship Maintenance',
  //       routerLink: '/relationship-maintenance',
  //       icon: 'group_edit',
  //     },
  //     {
  //       label: 'MVR Score',
  //       routerLink: '/mvr-score',
  //       icon: 'line_chart',
  //     },
  //     {
  //       label: 'Home',
  //       routerLink: '/home',
  //       icon: 'home',
  //     },
  //     {
  //       label: 'Error',
  //       routerLink: '/error',
  //       icon: 'error',
  //     },
  //     {
  //       label: 'My Profile',
  //       routerLink: '/my-profile',
  //       icon: 'user',
  //     },
  //     {
  //       label: 'Employee Info. & Driver License',
  //       routerLink: '/employee-information-and-driver-license',
  //       icon: 'parent',
  //     },
  //   ];

  //   if (roles.includes('CCSP_Admin')) {
  //     this.sharedService.logger(
  //       'App Component nav items to show - show all items',
  //       'ctry state, reltn main, mvr score, param main, model code, veh req, veh his, emp dl, cons admn, my prof'
  //     );
  //   } else if (roles.includes('CCSP_Specialist')) {
  //     this.sharedService.logger(
  //       'App Component nav items to show - show specialist items',
  //       'model code, veh his, emp dl, cons admn, my prof'
  //     );
  //   } else if (roles.includes('VA_Specialist')) {
  //    this.sharedService.logger(
  //       'App Component nav items to show - show specialist items', 'veh req, veh his, emp dl, my prof');
  //   } else {
  //     this.sharedService.logger(''App Component nav items to show - show remaining', 'veh his, emp dl, my prof');
  //   }
  // }

  setNavigationItems(roles: string[] | null | undefined): void {
    const userRole = (roles ?? []).map((r) => r.toLowerCase());

    const ALWAYS = [
      'home',
      'employeeInformationAndDriverLicense',
      'mvrScore',

      //  'myProfile'
      //
    ];
    const DEFAULT_SET = [
      'employeeInformationAndDriverLicense',
      'mvrScore',
      // 'vehicleHistory',
      // 'myProfile',
      //
    ];

    const ROLE_RULES: Record<string, string[]> = {
      ccsp_admin: [
        'countryStateCodeMaintenance',
        'relationshipMaintenance',
        'parameterMaintenance',
        'consentAdministration',
        'employeeDetails',
        // 'modelCodeMaintenance',
        // 'vehicleHistory',
      ],
      ccsp_specialist: [
        'employeeInformationAndDriverLicense',
        'consentAdministration',
        'employeeDetails',
        // 'vehicleHistory',
        // 'modelCodeMaintenance',
      ],

      va_specialist: [
        // 'employeeInformationAndDriverLicense',
        // 'vehicleRequests',
        // 'vehicleHistory',
      ],

      // add new roles here
    };

    const ITEMS: Record<
      string,
      {
        group: 'User' | 'Maintenance' | 'Administration';
        item: DsNavigationItem;
      }
    > = {
      home: {
        group: 'User',
        item: { label: 'Home', routerLink: '/home', icon: 'home' },
      },
      error: {
        group: 'User',
        item: { label: 'Error', routerLink: '/error', icon: 'error' },
      },
      myProfile: {
        group: 'User',
        item: { label: 'My Profile', routerLink: '/my-profile', icon: 'user' },
      },
      employeeInformationAndDriverLicense: {
        group: 'User',
        item: {
          label: 'Employee Info. & Driver License',
          routerLink: '/employee-information-and-driver-license',
          icon: 'parent',
        },
      },

      countryStateCodeMaintenance: {
        group: 'Maintenance',
        item: {
          label: 'Country State Code Maintenance',
          routerLink: '/country-state-code-maintenance',
          icon: 'globe',
        },
      },
      modelCodeMaintenance: {
        group: 'Maintenance',
        item: {
          label: 'Model Code Maintenance',
          routerLink: '/model-code-maintenance',
          icon: 'list_edit',
        },
      },
      parameterMaintenance: {
        group: 'Maintenance',
        item: {
          label: 'Parameter Maintenance',
          routerLink: '/parameter-maintenance',
          icon: 'database_edit',
        },
      },
      relationshipMaintenance: {
        group: 'Maintenance',
        item: {
          label: 'Relationship Maintenance',
          routerLink: '/relationship-maintenance',
          icon: 'group_edit',
        },
      },
      mvrScore: {
        group: 'User',
        item: {
          label: 'MVR Score',
          routerLink: '/mvr-score',
          icon: 'line_chart',
        },
      },

      consentAdministration: {
        group: 'Administration',
        item: {
          label: 'Consent Administration',
          routerLink: '/consent-administration',
          icon: 'file_user',
        },
      },
      employeeDetails: {
        group: 'Administration',
        item: {
          label: 'Employee Details',
          routerLink: '/employee-details',
          icon: 'group',
        },
      },
      vehicleHistory: {
        group: 'Administration',
        item: {
          label: 'Vehicle History',
          routerLink: '/vehicle-history',
          icon: 'calendar_car',
        },
      },
      vehicleRequests: {
        group: 'Administration',
        item: {
          label: 'Vehicle Requests',
          routerLink: '/vehicle-requests',
          icon: 'file_list',
        },
      },
    };

    const ORDER: Record<'User' | 'Maintenance' | 'Administration', string[]> = {
      User: [
        'home',
        'employeeInformationAndDriverLicense',
        'error',
        'myProfile',
        'mvrScore',
      ],
      Maintenance: [
        'countryStateCodeMaintenance',
        'parameterMaintenance',
        'relationshipMaintenance',
        'modelCodeMaintenance',
        // 'modelCodeMaintenance',
      ],
      Administration: [
        'consentAdministration',
        'vehicleHistory',
        'vehicleRequests',
        'employeeDetails',
        // 'vehicleHistory',
        // 'vehicleRequests',
      ],
    };

    const pick: string[] = [];
    for (const k of ALWAYS) if (!pick.includes(k)) pick.push(k);
    let matched = false;
    for (const role of userRole) {
      const list = ROLE_RULES[role];
      if (list && list.length) {
        matched = true;
        for (const k of list) if (!pick.includes(k)) pick.push(k);
      }
    }
    if (!matched)
      for (const k of DEFAULT_SET) if (!pick.includes(k)) pick.push(k);

    const user: DsNavigationItem[] = [];
    const maintenance: DsNavigationItem[] = [];
    const admin: DsNavigationItem[] = [];

    const pushOrdered = (keys: string[], target: DsNavigationItem[]) => {
      for (const k of keys) {
        if (pick.includes(k)) {
          const def = ITEMS[k];
          if (def) target.push(def.item);
        }
      }
    };

    pushOrdered(ORDER.User, user);
    pushOrdered(ORDER.Maintenance, maintenance);
    pushOrdered(ORDER.Administration, admin);

    const result: DsNavigationItem[] = [];
    if (user.length)
      result.push({ label: 'User', group: true, children: user });
    if (maintenance.length)
      result.push({ label: 'Maintenance', group: true, children: maintenance });
    if (admin.length)
      result.push({ label: 'Administration', group: true, children: admin });

    this.sharedService.logger('App Component nav items to show', result);

    this.navigationItems = result;
  }

  title = 'Company Car Self Service Portal';
}
